import React, { useEffect, useState } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { buildListingShopPath } from "../../utils/functionalOperations";
import { BASE_URL_TEST } from "../../utils/baseUrl";
import { urls } from "../../urls/urls";
import { useSelector } from "react-redux";

const ShopCard = ({ shop }) => {
  const navigate = useNavigate();

  const handleShopNow = () => {
    navigate(buildListingShopPath(shop), { state: { shop } });
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 4,
      }}
    >
      <Card
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          padding: 2,
          width: "100%",
          maxWidth: { xs: "100%", md: 1280 },
          boxShadow: 3,
          borderRadius: 2,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            padding: "4px 8px",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <StarIcon sx={{ color: "#FFA500", fontSize: 18 }} />
          <Typography
            variant="body2"
            sx={{ marginLeft: 0.5, color: "#FFA500" }}
          >
            {shop.rating}
          </Typography>
        </Box>
        <CardMedia
          component="img"
          sx={{
            width: { xs: "100%", sm: 300 },
            height: { xs: 180, sm: 220 },
            borderRadius: 2,
            objectFit: "cover",
          }}
          image={shop.image}
          alt={shop.name}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            marginLeft: { xs: 0, sm: 2 },
            marginTop: { xs: 2, sm: 0 },
          }}
        >
          <CardContent>
            <Typography variant="h6" component="div">
              {shop.shopName}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: "5px",
                marginBottom: 1,
                backgroundColor: "#FFF4E5",
                color: "#FF6F00",
              }}
            >
              {shop.description}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                marginTop: 1,
              }}
            >
              <LocationOnIcon
                fontSize="small"
                sx={{ color: "#FF6F00", marginRight: 0.5 }}
              />
              {shop.distance}
              {/* <Typography
                variant="body2"
                sx={{
                  marginLeft: "auto",
                  color: "#007BFF",
                }}
              >
                Distance: Dynamic
              </Typography> */}
            </Typography>
          </CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginTop: "auto",
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#FF6F00",
                color: "#fff",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#E65C00",
                },
              }}
              size="small"
              onClick={handleShopNow}
            >
              Shop Now
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

const Toplist = () => {
  const { state } = useLocation();
  const { category } = useParams();
  const latitude = useSelector(
    (reduxState) => reduxState.user?.userLocation?.latitude ?? null,
  );
  const longitude = useSelector(
    (reduxState) => reduxState.user?.userLocation?.longitude ?? null,
  );
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const categoryId = state?.categoryName || category;

    if (!latitude || !longitude || !categoryId) {
      setVendors([]);
      return;
    }

    const fetchVendors = async () => {
      setIsLoading(true);
      try {
        const vendorUrl = `${BASE_URL_TEST}${urls("getNearbyVendors")}?lat=${latitude}&lng=${longitude}&radius=20&vtype=listing&categoryids=${categoryId}`;
        const response = await fetch(vendorUrl);

        if (!response.ok) {
          throw new Error("Unable to fetch listing vendors");
        }

        const data = await response.json();
        const formattedVendors = (data?.vendors || []).map((item) => ({
          id: item.id,
          name: item.name,
          shopName: item.shop_name,
          location: item.location || item.city,
          city: item.city,
          listing: item.listing,
          listingName: item.listing?.name,
          category: item.listing?.name,
          description: item.listing?.name || "",
          image: item.store_image,
          distance: item.distance
            ? `${item.distance} kms near to your Location`
            : "N/A",
          rating: "4.8",
          address: `${item.street}, ${item.city}, ${item.state}, ${item.country}, ${item.pincode}`,
          openTime: item.open_time,
          closeTime: item.close_time,
          latitude: item.latitude,
          longitude: item.longitude,
          phoneNumber: item.phone_number,
          regularListType: item.reg_list_type,
        }));

        setVendors(formattedVendors);
      } catch (error) {
        console.error("Failed to fetch Toplist vendors:", error);
        setVendors([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVendors();
  }, [category, latitude, longitude, state?.categoryName]);

  return (
    <Box sx={{ maxWidth: "1200px", margin: "0 auto", padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "1rem", sm: "1.5rem", md: "1.75rem", lg: "1.7rem" },
            marginLeft: { xs: 0, md: 15, lg: 4 },
            marginTop: { xs: 0, md: 7 },
          }}
        >
          {/* {displayName.charAt(0).toUpperCase() + displayName.slice(1)} */}
        </Typography>
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          marginBottom: 2,
          color: "#333",
          ml: { xs: 2, sm: 1, md: 5 },
        }}
      >
        Vendor Details
      </Typography>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : vendors.length > 0 ? (
        <Grid container spacing={1}>
          {vendors.map((shop) => (
            <Grid item xs={12} key={shop.id}>
              <ShopCard shop={shop} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography sx={{ textAlign: "center", mt: 4, color: "gray" }}>
          No vendors found.Currently, we have services available only in the Secunderabad  location.
        </Typography>
      )}
    </Box>
  );
};

export default Toplist;
